/**
 * Typed wrappers around the `/moderator/*` endpoints. Mirrors the
 * shape of `galleryApi.ts` — same fetch helper, same error class.
 *
 * Every call requires a moderator/admin session cookie; a 403 means
 * the caller's role is wrong. The dashboard UI hides the entry
 * point unless `useCurrentUser().role` is `moderator` or `admin`,
 * but every endpoint also enforces the gate server-side.
 */

import { AuthApiError, apiBaseUrl } from '../account/authApi';

export interface ModeratorSubmissionRow {
  id: string;
  project_id: string;
  project_name: string;
  project_kind: 'image' | 'audio' | 'pose';
  submitter_email: string;
  status: 'pending' | 'processing' | 'approved' | 'rejected' | 'withdrawn';
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuditEntry {
  id: string;
  blob_id: string | null;
  stage: string;
  provider: string;
  decision: string;
  reason: string;
  signals: Record<string, unknown>;
  created_at: string;
}

export interface BlobEntry {
  id: string;
  class_label: string;
  kind: 'image' | 'audio';
  content_type: string;
  size_bytes: number;
  sha256: string;
}

export interface ReportEntry {
  id: string;
  reporter_id: string | null;
  reason: string;
  detail: string | null;
  created_at: string;
}

export type DecisionPayload = {
  decision: 'approve' | 'reject';
  reason?: string;
};

async function jsonRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${apiBaseUrl()}${path}`;
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(url, { ...init, headers, credentials: 'include' });
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Empty / non-JSON — fall through.
  }
  if (!response.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body && typeof body.detail === 'string'
        ? body.detail
        : null;
    throw new AuthApiError(
      response.status,
      detail,
      `Request to ${path} failed (${response.status}).`,
    );
  }
  return body as T;
}

export async function listModeratorSubmissions(
  statusFilter?: ModeratorSubmissionRow['status'],
): Promise<ModeratorSubmissionRow[]> {
  const qs = statusFilter ? `?status_filter=${encodeURIComponent(statusFilter)}` : '';
  return jsonRequest<ModeratorSubmissionRow[]>(`/moderator/submissions${qs}`);
}

export async function getSubmissionAudit(submissionId: string): Promise<AuditEntry[]> {
  return jsonRequest<AuditEntry[]>(
    `/moderator/submissions/${encodeURIComponent(submissionId)}/audit`,
  );
}

export async function getSubmissionBlobs(submissionId: string): Promise<BlobEntry[]> {
  return jsonRequest<BlobEntry[]>(
    `/moderator/submissions/${encodeURIComponent(submissionId)}/blobs`,
  );
}

export async function getSubmissionReports(submissionId: string): Promise<ReportEntry[]> {
  return jsonRequest<ReportEntry[]>(
    `/moderator/submissions/${encodeURIComponent(submissionId)}/reports`,
  );
}

export async function decideSubmission(
  submissionId: string,
  payload: DecisionPayload,
): Promise<{ status: string }> {
  return jsonRequest<{ status: string }>(
    `/moderator/submissions/${encodeURIComponent(submissionId)}/decision`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

/** URL the moderator UI uses to render a blob preview. The browser
 *  calls this directly with credentials so the cookie rides along. */
export function blobRawUrl(blobId: string): string {
  return `${apiBaseUrl()}/moderator/blobs/${encodeURIComponent(blobId)}/raw`;
}

// --- Suspension + appeals ---

export interface SuspendedUserSummary {
  id: string;
  email: string;
  role: string;
  suspended_at: string | null;
}

export interface AppealRow {
  id: string;
  user_id: string;
  user_email: string;
  message: string;
  decision: 'upheld' | 'overturned' | null;
  decision_note: string | null;
  resolved_by: string | null;
  resolver_email: string | null;
  created_at: string;
  resolved_at: string | null;
}

export type AppealResolution = {
  decision: 'upheld' | 'overturned';
  note?: string;
};

export async function listSuspendedUsers(): Promise<SuspendedUserSummary[]> {
  return jsonRequest<SuspendedUserSummary[]>('/moderator/suspended-users');
}

export async function suspendUser(userId: string, reason?: string): Promise<SuspendedUserSummary> {
  const body: Record<string, unknown> = {};
  if (reason) body.reason = reason;
  return jsonRequest<SuspendedUserSummary>(
    `/moderator/users/${encodeURIComponent(userId)}/suspend`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function unsuspendUser(userId: string): Promise<SuspendedUserSummary> {
  return jsonRequest<SuspendedUserSummary>(
    `/moderator/users/${encodeURIComponent(userId)}/unsuspend`,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

export async function listAppeals(pendingOnly = true): Promise<AppealRow[]> {
  const qs = pendingOnly ? '?pending_only=true' : '?pending_only=false';
  return jsonRequest<AppealRow[]>(`/moderator/appeals${qs}`);
}

export async function resolveAppeal(
  appealId: string,
  payload: AppealResolution,
): Promise<AppealRow> {
  return jsonRequest<AppealRow>(`/moderator/appeals/${encodeURIComponent(appealId)}/resolve`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
